import type { VariablesOf } from "@/data-access/graphql";
import {
  ArchiveBriefingTemplate,
  CreateTemplateLayout,
  GetTemplateLayout,
  ListTemplateLayouts,
  PublishBriefingTemplate,
  UpdateTemplateLayout,
} from "@/data-access/scheduled-briefings/queries/template-layout-queries";
import { validateTemplateLayout } from "@/lib/template-layout/validateTemplateLayout";
import {
  mapGetTemplateLayoutResponse,
  mapListTemplateLayoutsResponse,
  mapOperationInfo,
  mapTemplateLayoutGraphQLError,
  mapTemplateLayoutMutationResponse,
} from "@/lib/template-studio/repository/graphqlTemplateLayoutMappers";
import type {
  TemplateLayoutListParams,
  TemplateLayoutRepository,
  TemplateLayoutRepositoryError,
  TemplateLayoutRepositoryResult,
} from "@/lib/template-studio/repository/templateLayoutRepository.types";
import { client } from "@/lib/urqlProvider";

type ListTemplateLayoutsVariables = VariablesOf<typeof ListTemplateLayouts>;
type BriefingTemplateStatus = NonNullable<
  NonNullable<ListTemplateLayoutsVariables["filters"]>["status"]
>;

const statusByRepositoryStatus = {
  archived: "ARCHIVED",
  draft: "DRAFT",
  published: "PUBLISHED",
} as const satisfies Record<
  NonNullable<TemplateLayoutListParams["status"]>,
  BriefingTemplateStatus
>;

const requestFailure = (message: string): TemplateLayoutRepositoryError => ({
  code: "REQUEST_FAILURE",
  message,
});

/** @public */
export const listGraphQLTemplateLayouts: TemplateLayoutRepository["list"] =
  async (_context, params) => {
    const variables: ListTemplateLayoutsVariables = {
      ...(params?.status !== undefined
        ? { filters: { status: statusByRepositoryStatus[params.status] } }
        : {}),
      ...(params?.first !== undefined ? { first: params.first } : {}),
      ...(params?.after != null ? { after: params.after } : {}),
    };

    try {
      const result = await client
        .query(ListTemplateLayouts, variables, {
          requestPolicy: "network-only",
        })
        .toPromise();

      if (result.error !== undefined) {
        return {
          success: false,
          error: mapTemplateLayoutGraphQLError(result.error),
        };
      }
      if (result.data === undefined) {
        return {
          success: false,
          error: requestFailure("Template list returned no data."),
        };
      }

      return mapListTemplateLayoutsResponse(result.data);
    } catch (error) {
      return {
        success: false,
        error: requestFailure(
          error instanceof Error ? error.message : "Template list failed.",
        ),
      };
    }
  };

/** @public */
export const getGraphQLTemplateLayout: TemplateLayoutRepository["get"] = async (
  _context,
  id,
) => {
  try {
    const result = await client
      .query(GetTemplateLayout, { id }, { requestPolicy: "network-only" })
      .toPromise();

    if (result.error !== undefined) {
      return {
        success: false,
        error: mapTemplateLayoutGraphQLError(result.error),
      };
    }
    if (result.data === undefined) {
      return {
        success: false,
        error: requestFailure("Template lookup returned no data."),
      };
    }

    return mapGetTemplateLayoutResponse(result.data);
  } catch (error) {
    return {
      success: false,
      error: requestFailure(
        error instanceof Error ? error.message : "Template lookup failed.",
      ),
    };
  }
};

/** @public */
export const createGraphQLTemplateLayout: TemplateLayoutRepository["create"] =
  async (_context, input) => {
    const document = validateTemplateLayout(input.document);
    if (!document.success) {
      return {
        success: false,
        error: {
          code: "INVALID_DOCUMENT",
          message: "Template document is invalid.",
          validationErrors: document.errors,
        },
      };
    }

    try {
      const result = await client
        .mutation(CreateTemplateLayout, {
          input: {
            name: input.name,
            document: document.data,
            ...(input.description !== undefined
              ? { description: input.description }
              : {}),
          },
        })
        .toPromise();

      if (result.error !== undefined) {
        return {
          success: false,
          error: mapTemplateLayoutGraphQLError(result.error),
        };
      }

      const payload = result.data?.createTemplateLayout;
      if (payload === undefined) {
        return {
          success: false,
          error: requestFailure("Template create returned no data."),
        };
      }

      return mapTemplateLayoutMutationResponse(payload);
    } catch (error) {
      return {
        success: false,
        error: requestFailure(
          error instanceof Error ? error.message : "Template create failed.",
        ),
      };
    }
  };

const runArchiveMutation = async (id: string, archived: boolean) => {
  if (archived) {
    const result = await client
      .mutation(ArchiveBriefingTemplate, { id })
      .toPromise();
    return {
      error: result.error,
      payload: result.data?.archiveBriefingTemplate,
    };
  }
  const result = await client
    .mutation(PublishBriefingTemplate, { id })
    .toPromise();
  return { error: result.error, payload: result.data?.publishBriefingTemplate };
};

/** Archives a template, or unarchives it by publishing it again. */
export const setGraphQLTemplateLayoutArchived = async (
  id: string,
  archived: boolean,
): Promise<TemplateLayoutRepositoryResult<null>> => {
  try {
    const { error, payload } = await runArchiveMutation(id, archived);
    if (error !== undefined) {
      return { success: false, error: mapTemplateLayoutGraphQLError(error) };
    }
    if (payload === undefined) {
      return {
        success: false,
        error: requestFailure("Template status change returned no data."),
      };
    }
    if (payload.__typename === "OperationInfo") {
      return { success: false, error: mapOperationInfo(payload) };
    }
    return { success: true, data: null };
  } catch (error) {
    return {
      success: false,
      error: requestFailure(
        error instanceof Error
          ? error.message
          : "Template status change failed.",
      ),
    };
  }
};

/** @public */
export const updateGraphQLTemplateLayout: TemplateLayoutRepository["update"] =
  async (_context, input) => {
    const document =
      input.document === undefined
        ? undefined
        : validateTemplateLayout(input.document);
    if (document !== undefined && !document.success) {
      return {
        success: false,
        error: {
          code: "INVALID_DOCUMENT",
          message: "Template document is invalid.",
          validationErrors: document.errors,
        },
      };
    }

    try {
      const result = await client
        .mutation(UpdateTemplateLayout, {
          input: {
            id: input.id,
            expectedUpdatedAt: input.expectedUpdatedAt,
            ...(input.name !== undefined ? { name: input.name } : {}),
            ...(input.description !== undefined
              ? { description: input.description }
              : {}),
            ...(document?.success === true ? { document: document.data } : {}),
          },
        })
        .toPromise();

      if (result.error !== undefined) {
        return {
          success: false,
          error: mapTemplateLayoutGraphQLError(result.error),
        };
      }

      const payload = result.data?.updateTemplateLayout;
      if (payload === undefined) {
        return {
          success: false,
          error: requestFailure("Template update returned no data."),
        };
      }

      return mapTemplateLayoutMutationResponse(payload);
    } catch (error) {
      return {
        success: false,
        error: requestFailure(
          error instanceof Error ? error.message : "Template update failed.",
        ),
      };
    }
  };
